import {
  Box,
  Flex,
  Img,
  Text,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { person } from "../defaultHeader/assets";
import SubHeading from "../typography/SubHeading";
import { verifiedBadgeIcon } from "../../assets";
import colors from "../../utils/colors";
import BodyText from "../typography/Body";
import { useAppSelector } from "../../hooks/useAppSelector";
import EditableProfilePicture from "./EditableProfilePicture";
import {
  TDoctorInfo,
  TClinicAddress,
  TAvailableTimeSlot,
} from "../../apis/types";

const getSafeProfilePicUrl = (
  profilePictureData: TDoctorInfo["profilePicture"]
): string | undefined => {
  if (!profilePictureData) {
    return undefined;
  }
  if (typeof profilePictureData === "string") {
    return profilePictureData;
  }
  if (
    typeof profilePictureData === "object" &&
    Object.prototype.hasOwnProperty.call(profilePictureData, "url") &&
    typeof (profilePictureData as { url?: string }).url === "string"
  ) {
    return (profilePictureData as { url: string }).url;
  }
  return undefined;
};

interface CompletenessResult {
  score: number;
  isLicenseVerified: boolean;
  incompleteItems: number;
  criticality: "high" | "medium" | "low";
}

const calculateProfileCompleteness = (
  doctorInfo: TDoctorInfo | null | undefined
): CompletenessResult => {
  if (!doctorInfo)
    return {
      score: 0,
      isLicenseVerified: false,
      incompleteItems: 10,
      criticality: "high",
    };

  let points = 0;
  const maxPoints = 10;
  let completedItemsForBadge = 0;
  let missingCritical = 0;

  const criticalChecks = {
    name: doctorInfo.name && doctorInfo.name.trim() !== "",
    title: doctorInfo.title && doctorInfo.title.trim() !== "",
    phoneNumber: doctorInfo.phoneNumber && doctorInfo.phoneNumber.trim() !== "",
    licenseNumber:
      doctorInfo.licenseNumber && doctorInfo.licenseNumber.trim() !== "",
    specialty:
      doctorInfo.specialty &&
      doctorInfo.specialty.length > 0 &&
      doctorInfo.specialty.some((s) => s && s.trim() !== ""),
  };

  Object.values(criticalChecks).forEach((isComplete) => {
    if (isComplete) {
      points++;
      completedItemsForBadge++;
    } else {
      missingCritical++;
    }
  });

  const profilePicUrl = getSafeProfilePicUrl(doctorInfo.profilePicture);
  if (profilePicUrl && profilePicUrl !== person) {
    points++;
    completedItemsForBadge++;
  }

  let isLicenseVerified = false;

  if (doctorInfo.licenseDocuments) {
    points++;
    completedItemsForBadge++;
    isLicenseVerified = true;
  }

  const hasCompleteClinicAddress = doctorInfo.clinics?.some(
    (addr: TClinicAddress) =>
      addr.name && addr.address && addr.city && addr.state
  );
  if (hasCompleteClinicAddress) {
    points++;
    completedItemsForBadge++;
  }

  const hasCompleteAvailability = doctorInfo.availability?.some(
    (slot: TAvailableTimeSlot) => slot.day && slot.startTime && slot.endTime
  );
  if (hasCompleteAvailability) {
    points++;
    completedItemsForBadge++;
  }

  if (doctorInfo.email && doctorInfo.email.trim() !== "") {
    points++;
    completedItemsForBadge++;
  }

  const score = Math.round((points / maxPoints) * 100);
  const incompleteItems = maxPoints - completedItemsForBadge;

  let criticality: "high" | "medium" | "low" = "low";
  if (missingCritical > 0) {
    criticality = "high";
  } else if (incompleteItems > 2) {
    criticality = "medium";
  }

  return { score, isLicenseVerified, incompleteItems, criticality };
};

const ProfileCard = () => {
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const [profilePicForDisplay, setProfilePicForDisplay] = useState(
    getSafeProfilePicUrl(doctorInfo?.profilePicture) || person
  );

  const [completeness, setCompleteness] = useState<CompletenessResult>(
    calculateProfileCompleteness(doctorInfo)
  );

  const cardBorderColor = useColorModeValue(colors.grey, "gray.600");
  const highCriticalityColor = colors.red;
  const mediumCriticalityColor = "#FFA500";
  const lowCriticalityColor = colors.primary;

  let currentBorderColor = cardBorderColor;
  let currentCompletenessColor = lowCriticalityColor;

  if (completeness.criticality === "high") {
    currentBorderColor = highCriticalityColor;
    currentCompletenessColor = highCriticalityColor;
  } else if (completeness.criticality === "medium") {
    currentBorderColor = mediumCriticalityColor;
    currentCompletenessColor = mediumCriticalityColor;
  }

  useEffect(() => {
    const newCompleteness = calculateProfileCompleteness(doctorInfo);
    setCompleteness(newCompleteness);

    const currentPicUrl = getSafeProfilePicUrl(doctorInfo?.profilePicture);
    setProfilePicForDisplay(currentPicUrl || person);
  }, [doctorInfo]);

  const handleCompleteNow = () => {
    navigate("/profile");
    setTimeout(() => {
      const element = document.getElementById("profile-sections-grid");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };
  // console.log(doctorInfo.licenseNumber);
  return (
    <Flex
      gap={"10px"}
      boxShadow={"1px 4px 15px 0px #3030301F"}
      direction={"column"}
      backgroundColor={colors.white}
      borderRadius={"8px"}
      borderWidth="2px"
      borderColor={currentBorderColor}
      py={"16px"}
      pl={"16px"}
      pr={"24px"}
      minW="300px"
    >
      <Flex mb={"16px"} justifyContent={"space-between"} alignItems={"center"}>
        <Flex gap={"8px"} alignItems={"center"}>
          <EditableProfilePicture
            doctorId={doctorInfo._id}
            currentImage={doctorInfo?.profilePicture || person}
          />

          <SubHeading size="s" noOfLines={1}>
            {doctorInfo?.title
              ? `${doctorInfo.title} ${
                  doctorInfo?.name?.split(" ")[0] || doctorInfo?.name || ""
                }`.trim()
              : doctorInfo?.name?.split(" ")[0] || doctorInfo?.name || "User"}
          </SubHeading>
        </Flex>
        {completeness.isLicenseVerified && (
          <Tooltip label="Medical License Verified" placement="top" hasArrow>
            <Img src={verifiedBadgeIcon} h="24px" w="24px" />
          </Tooltip>
        )}
      </Flex>

      <Box
        w={"full"}
        backgroundColor={colors.lightGrey}
        borderRadius={"5px"}
        height={"10px"}
        mb={1}
      >
        <Box
          w={`${completeness.score}%`}
          backgroundColor={currentCompletenessColor}
          borderRadius={"5px"}
          height={"10px"}
          transition="width 0.5s ease-in-out, background-color 0.3s ease-in-out"
        ></Box>
      </Box>
      <Flex gap={"5px"} alignItems="center">
        <BodyText size="xs" color={currentCompletenessColor}>
          Your profile is {completeness.score}% complete.
        </BodyText>
        {completeness.score < 100 && (
          <Text
            fontSize="xs"
            color={currentCompletenessColor}
            fontWeight="medium"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={handleCompleteNow}
          >
            Complete now
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

export default ProfileCard;
